import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

/* Link that supports internal routes (to) and external URLs (href) */
export const CtaLink = ({ cta, className = "", children, ...rest }) => {
  if (cta.href) {
    return (
      <a href={cta.href} target={cta.external ? "_blank" : undefined} rel={cta.external ? "noreferrer" : undefined} className={className} {...rest}>
        {children || cta.label}
      </a>
    );
  }
  return (
    <Link to={cta.to} className={className} {...rest}>
      {children || cta.label}
    </Link>
  );
};

/* Scroll-reveal wrapper */
export const Reveal = ({ children, className = "", delay = 0, as: Tag = "div", ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export const SectionHeader = ({ eyebrow, title, subtitle, align = "left", className = "" }) => (
  <Reveal className={`${align === "center" ? "text-center mx-auto" : ""} max-w-3xl ${className}`}>
    {eyebrow && <span className="eyebrow">{eyebrow}</span>}
    <h2 className={`h-section ${eyebrow ? "mt-4" : ""}`}>{title}</h2>
    {subtitle && <p className="mt-5 text-lg text-[var(--fr-ink-2)] leading-relaxed">{subtitle}</p>}
  </Reveal>
);

export const PageHero = ({ eyebrow, title, subtitle, primary, secondary, children }) => (
  <section className="pt-20 pb-16 md:pt-28 md:pb-20">
    <div className="container-x">
      {eyebrow && <span className="eyebrow-pill fade-up">{eyebrow}</span>}
      <h1 className="h-display mt-6 max-w-4xl fade-up delay-1">{title}</h1>
      {subtitle && (
        <p className="mt-6 max-w-2xl text-lg md:text-xl text-[var(--fr-ink-2)] leading-relaxed fade-up delay-2">
          {subtitle}
        </p>
      )}
      {(primary || secondary) && (
        <div className="mt-8 flex flex-wrap gap-3 fade-up delay-3">
          {primary && (
            <Link to={primary.to} className="btn btn-primary btn-lg" data-testid="hero-primary-cta">
              {primary.label}
            </Link>
          )}
          {secondary && (
            <Link to={secondary.to} className="btn btn-outline btn-lg" data-testid="hero-secondary-cta">
              {secondary.label}
            </Link>
          )}
        </div>
      )}
      {children}
    </div>
  </section>
);

export const CheckItem = ({ children }) => (
  <li className="flex items-start gap-3 text-[15px] leading-snug">
    <Check size={16} className="mt-[3px] text-[var(--fr-primary)] flex-shrink-0" strokeWidth={2.5} />
    <span>{children}</span>
  </li>
);

export const ArrowLink = ({ to, children, className = "", ...rest }) => (
  <Link
    to={to}
    className={`inline-flex items-center gap-1 text-sm font-semibold text-[var(--fr-primary-dark)] hover:gap-2 transition-[gap] ${className}`}
    {...rest}
  >
    {children} <ArrowRight size={15} />
  </Link>
);

export const DarkCTA = ({ title, text, cta, secondary, email, id, eyebrow }) => (
  <section className="section pt-0" id={id}>
    <div className="container-x">
      <Reveal className="dark-block px-6 py-16 md:px-16 md:py-24 text-center" data-testid="final-cta">
        {eyebrow && <span className="eyebrow text-white/60 block mb-5">{eyebrow}</span>}
        <h2 className="h-section text-white">{title}</h2>
        <p className="mt-5 text-lg text-white/70 max-w-xl mx-auto leading-relaxed">{text}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <CtaLink cta={cta} className="btn btn-primary btn-lg" data-testid="final-cta-button" />
          {secondary && <CtaLink cta={secondary} className="btn btn-ghost-dark btn-lg" data-testid="final-cta-secondary" />}
        </div>
        {email && (
          <a href={`mailto:${email}`} className="mt-6 inline-block text-sm text-white/60 hover:text-white">
            {email}
          </a>
        )}
      </Reveal>
    </div>
  </section>
);

/* Simple FAQ accordion (khufu style, plain rows) */
export const FaqList = ({ items, testid = "faq" }) => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="divide-y divide-[var(--fr-border)] border-y border-[var(--fr-border)]" data-testid={testid}>
      {items.map((f, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={f.q}>
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? -1 : i)}
              className="w-full flex items-start justify-between gap-6 py-5 text-left"
              aria-expanded={isOpen}
              data-testid={`${testid}-item-${i}`}
            >
              <span className="font-display font-semibold text-[17px] leading-snug">{f.q}</span>
              <span
                className={`mt-[2px] flex-shrink-0 w-6 h-6 rounded-full border border-[var(--fr-border)] flex items-center justify-center text-[var(--fr-ink-2)] transition-transform duration-300 ${
                  isOpen ? "rotate-45 bg-[var(--fr-primary-tint)] border-transparent text-[var(--fr-primary-dark)]" : ""
                }`}
              >
                +
              </span>
            </button>
            <div
              className="grid transition-[grid-template-rows] duration-300 ease-out"
              style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <p className="pb-6 pr-12 text-[15px] text-[var(--fr-ink-2)] leading-relaxed">{f.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
